import { ConfirmationDialog } from '@statsbomb/kitbag-components';
import { MetricDistributionRange } from '@statsbomb/parachute-types';
import { useAtomValue, useSetAtom } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { batchTrackMetricFilters } from '@/analytics/snowplow/tracking';
import { isLoadFilterSetModalOpenAtom } from '@/atoms/filters/userFilters';
import { FilterSetTable } from '@/components/tables/FilterSetTable/FilterSetTable';
import { userConfigActionAtoms } from '@/consts/scope';
import { useFilterSets } from '@/hooks/useFilterSets';
import { useFilterSetType } from '@/hooks/useFilterSetType';
import { useNavigateToFilterSetId } from '@/hooks/useNavigateToFilterSetId';

const LoadFilterSetModalWithData = () => {
  const { t } = useTranslation(['filters', 'general', 'table']);
  const filterSets = useFilterSets(useFilterSetType());
  const [selectedFilterIdToLoad, setSelectedFilterIdToLoad] = useState<string | null>(null);
  const setIsLoadModalOpen = useSetAtom(isLoadFilterSetModalOpenAtom);
  const navigateToFilterSetId = useNavigateToFilterSetId(undefined, () => setIsLoadModalOpen(false));

  const onConfirm = async () => {
    // find the selected filter set, extract the metric distributions names and fire a tracking event for each one.
    const selectedFilterSetConfig = filterSets.find(filterSet => filterSet.configId === selectedFilterIdToLoad);
    const selectedMetricDistributions: string[] =
      (selectedFilterSetConfig?.definition.filters.metricDistributions as MetricDistributionRange[] | undefined)?.map(
        metricDistribution => metricDistribution.name,
      ) ?? [];

    await batchTrackMetricFilters(selectedMetricDistributions, true);
    navigateToFilterSetId(selectedFilterIdToLoad);
  };

  const dialogId = 'load-filter-set-modal';
  return (
    <ConfirmationDialog
      id={dialogId}
      testId={dialogId}
      title={t('savedFilters')}
      isOpen
      onConfirm={onConfirm}
      onCancel={() => setIsLoadModalOpen(false)}
      cancelLabel={t('cancel', { ns: 'general' })}
      confirmLabel={t('load', { ns: 'general' })}
      isDisabledConfirm={selectedFilterIdToLoad === null || filterSets.length === 0}
    >
      <div className="w-screen-3/4 lg:!w-screen-1/2">
        <FilterSetTable selectedFilterIdToLoad={selectedFilterIdToLoad} onClick={setSelectedFilterIdToLoad} />
      </div>
    </ConfirmationDialog>
  );
};

export const LoadFilterSetModal = () => {
  const isLoadModalOpen = useAtomValue(isLoadFilterSetModalOpenAtom);

  return (
    <ScopeProvider atoms={userConfigActionAtoms}>
      {isLoadModalOpen && (
        <Suspense>
          <LoadFilterSetModalWithData />
        </Suspense>
      )}
    </ScopeProvider>
  );
};
